import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { type SmartResponse, fetchSmart } from "../api";
import { Card, Pill, SectionTitle } from "./ui";

/** Prognose: the engine's slimme momenten, only when the customer
 *  switched them on (Account > Slimme apparaten). */
function fmt(ts: number): string {
  const d = new Date(ts * 1000);
  const day = d.toLocaleDateString("nl-NL", { weekday: "short" });
  const time = d.toLocaleTimeString("nl-NL", { hour: "2-digit", minute: "2-digit" });
  const today = new Date().toDateString() === d.toDateString();
  return today ? time : `${day} ${time}`;
}
const TIER: Record<string, "accent" | "mute" | "danger"> = { goedkoop: "accent", normaal: "mute", duur: "danger" };
const HEAT: Record<string, string> = { bijstoken: "Bijstoken (goedkoop uur)", normaal: "Normaal", temperen: "Temperen (duur uur)" };

export function SmartMomentsCard() {
  const [data, setData] = useState<SmartResponse | null>(null);
  useEffect(() => {
    let cancelled = false;
    const load = () => fetchSmart().then((d) => { if (!cancelled) setData(d); }).catch(() => {});
    load();
    const id = window.setInterval(load, 5 * 60_000);
    return () => { cancelled = true; window.clearInterval(id); };
  }, []);
  if (!data || !data.enabled || !data.available) return null;
  return (
    <section className="flex flex-col gap-2">
      <SectionTitle>Slimme momenten</SectionTitle>
      <Card className="p-4 flex flex-col gap-3">
        <div className="flex items-center gap-2 flex-wrap text-sm">
          {data.price_tier_now && <Pill tone={TIER[data.price_tier_now]}>stroom nu {data.price_tier_now}</Pill>}
          {data.best_2h && (
            <span className="text-xs text-ink-mute">
              goedkoopste 2 uur: {fmt(data.best_2h.start)} tot {fmt(data.best_2h.end)}{data.best_2h.forecast ? " (verwachting)" : ""}
            </span>
          )}
          {data.negative_price?.now && <Pill tone="accent">negatieve prijs: alles mag aan</Pill>}
        </div>
        {data.heating && (
          <div className="text-sm">
            <span className="text-ink-mute">Verwarming: </span>{HEAT[data.heating.advice]}
            {data.heating.until && <span className="text-xs text-ink-mute"> tot {fmt(data.heating.until)}</span>}
          </div>
        )}
        {data.loads.map((l) => (
          <div key={l.id} className="flex items-start justify-between gap-3 border-t border-card-border pt-2">
            <div className="min-w-0">
              <div className="text-sm font-medium truncate">{l.name}</div>
              {l.available ? (
                <div className="text-xs text-ink-mute">
                  {l.windows.map((w, k) => <span key={k}>{k > 0 ? ", " : ""}{fmt(w.start)} tot {fmt(w.end)}</span>)}
                  {l.reason ? ` · ${l.reason}` : ""}{l.forecast ? " · verwachting" : ""}
                  {l.saving_eur != null && l.saving_eur > 0.005 && ` · bespaart ${l.saving_eur.toFixed(2).replace(".", ",")} EUR`}
                </div>
              ) : (
                <div className="text-xs text-ink-mute">geen passend moment in de planning</div>
              )}
              {l.stats?.running_since != null ? (
                <div className="text-xs text-ink-mute">loopt nu sinds {fmt(l.stats.running_since)}</div>
              ) : l.stats?.last_run ? (
                <div className="text-xs text-ink-mute">
                  laatst {fmt(l.stats.last_run.started_at)}
                  {l.stats.last_run.in_window != null && (l.stats.last_run.in_window ? ", aanbevolen moment" : ", ander moment")}
                  {l.stats.last_run.kwh != null && `, ${l.stats.last_run.kwh.toFixed(1).replace(".", ",")} kWh`}
                </div>
              ) : null}
            </div>
            {l.active_now && <Pill tone="accent">nu</Pill>}
          </div>
        ))}
        <Link to="/account/slim" className="text-xs text-accent">Apparaten instellen</Link>
      </Card>
    </section>
  );
}
